import React from 'react';
import { objectUtils, commonEnums } from '@codametrix/ui-common';
import { MedicalCodeChip } from '@codametrix/ui-components';
import classnames from 'classnames';

const { ChargeSessionCodeType } = commonEnums;

const chipTypeMap = {
  procedure: ChargeSessionCodeType.PROCEDURE,
  diagnosis: ChargeSessionCodeType.DIAGNOSIS,
  modifier: ChargeSessionCodeType.MODIFIER
} as const;

export const prop = (
  caseRun: CMxAPI.RelatedCase,
  propName: string,
  optDefault = 'N/A'
) => {
  if (caseRun === null || caseRun === undefined) {
    return optDefault;
  }
  return objectUtils.get(caseRun, propName, optDefault) || optDefault;
};

/**
 * Function to format cell data with optional capitalization.
 * @function
 * @param {string} data - The input data to be formatted.
 * @param {boolean} shouldCapitalize - Flag indicating whether to capitalize the data.
 * @returns {React.ReactNode} - The formatted cell data.
 */
export const getCellData = (
  data: string,
  shouldCapitalize: boolean,
  styleClasses?: string[]
) => {
  let title;
  if (data === null || data === undefined) {
    return <></>;
  }
  if (data && data?.length >= 10) {
    title = data;
  }
  if (title) {
    return (
      <span title={title} className={classnames(styleClasses)}>
        {shouldCapitalize ? objectUtils.capitalize(data) : data}
      </span>
    );
  } else {
    return (
      <span className={classnames(styleClasses)}>
        {shouldCapitalize ? objectUtils.capitalize(data) : data}
      </span>
    );
  }
};

const renderChip = (
  key: string,
  type: "procedure" | "diagnosis" | "modifier",
  code: string | number
) => (
  <MedicalCodeChip
    key={key}
    code={code}
    codeType={chipTypeMap[type]}
    editable={false}
  />
);

export const getSubmittedInvoiceCodes = (
  status: string,
  invoices: InvoicesAPI.Invoice[] | unknown
) => {
  if (status !== "Sent to Billing (CMX)" || !Array.isArray(invoices)) {
    return null;
  }

  const submittedInvoice = invoices.find((inv) => inv.type === "SUBMITTED");
  if (!submittedInvoice || !Array.isArray(submittedInvoice.transactions)) {
    return null;
  }

  const rows = submittedInvoice.transactions.map((txn, txnIndex) => {
    const procedureChips =
      txn.procedure?.procedure_code != null
        ? [renderChip(`proc-${txnIndex}`, "procedure", txn.procedure.procedure_code)]
        : [];

    const diagnosisChips = (txn.diagnoses ?? [])
      .filter((d) => d.diagnosis_code != null)
      .map((d, diagIndex) =>
        renderChip(`diag-${txnIndex}-${diagIndex}`, "diagnosis", d.diagnosis_code!)
      );

    const modifierChips = (txn.modifiers ?? [])
      .filter((m) => m.modifier_code != null)
      .map((m, modIndex) =>
        renderChip(`mod-${txnIndex}-${modIndex}`, "modifier", m.modifier_code!)
      );

    return (
      <div key={`txn-row-${txnIndex}`} className="codes-row">
        <div className="codes-section">{procedureChips}</div>
        <div className="codes-section">{modifierChips}</div>
        <div className="codes-section">{diagnosisChips}</div>
      </div>

    );
  });

  return rows.length > 0 ? (
      <div className="codes-grid">{rows}</div>
  ) : null;
};